import { useEffect, useState, type ReactNode } from 'react'
import { useAuth } from '../auth/useAuth'
import { EntitlementsProvider } from '../platform/access/useEntitlement'
import { appUserIdFor } from './appUserId'
import { grantedFromActive } from './grantedFromRevenueCat'
import type { BillingPort } from './port'

/**
 * Owns `EntitlementsProvider` (#102): feeds `granted` from RevenueCat customer
 * info for the signed-in user. No call site changes — gates still read
 * `useEntitlement`.
 *
 * **Fails closed.** No port (dummy / missing key), an SDK error, a network
 * failure: `granted` is the baseline only and nothing throws at the page.
 *
 * **Loading is its own state.** While a port is asked for a signed-in user and
 * has not answered, `loading` is true: gates still read locked, but
 * `PremiumGate` shows a placeholder and announces nothing, so a subscriber does
 * not see a paywall flash on every load. A FAILED read settles as ready and
 * locked. No port means ready at once.
 *
 * **Per person.** The identity is the signed-in user's own id; there is no
 * household inheritance and no seam for one (owner decision 2026-10-31).
 *
 * **No leak between users.** Loaded entitlements are tagged with the app user
 * id they were fetched for and read only while that is still the current user,
 * so on sign-out or an account switch the previous user's set is gone in the
 * SAME render — not after an effect has run.
 *
 * `baseline` is what the build itself grants (the dev tier and its override,
 * `entitlementsForTier`); billing adds to it and can never take it away. That
 * is why `main.tsx` passes no port when the developer override differs from the
 * build's tier: a forced Free would otherwise still show a subscriber's grants.
 */

type Loaded = { appUserId: string; granted: readonly string[] }

export function BillingProvider({
  port,
  baseline = [],
  children,
}: {
  port: BillingPort | null
  baseline?: readonly string[]
  children: ReactNode
}) {
  const auth = useAuth()
  const appUserId = auth.status === 'signedIn' ? appUserIdFor(auth.profile.id) : null
  const [loaded, setLoaded] = useState<Loaded | null>(null)

  useEffect(() => {
    if (port === null) return
    if (appUserId === null) {
      setLoaded(null)
      port.reset().catch(() => undefined)
      return
    }
    // Cleanup invalidates this request: a slow answer for a previous user (or a
    // superseded effect run) must not land after a newer one.
    let live = true
    port
      .identify(appUserId)
      .then((ids) => grantedFromActive(ids))
      // Locked, not thrown: billing being down must not take the page with it.
      // Settled all the same, or the failure would read as loading forever.
      .catch((): readonly string[] => [])
      .then((granted) => {
        if (live) setLoaded({ appUserId, granted })
      })
    return () => {
      live = false
    }
  }, [port, appUserId])

  const settled = loaded !== null && loaded.appUserId === appUserId
  const loading = port !== null && appUserId !== null && !settled
  const current = settled ? loaded.granted : []
  // No memo: EntitlementsProvider keys on the joined value, not identity.
  const granted = [...baseline, ...current]

  return (
    <EntitlementsProvider granted={granted} loading={loading}>
      {children}
    </EntitlementsProvider>
  )
}
